import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import { ArrowLeft, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { environment } from '../config/environment';
import { getApiHeaders, getApiHeadersForGet } from '../utils/apiUtils';

interface SeoConfig {
  id?: string;
  title?: string;
  keywords?: string;
  description?: string;
  image_path?: string;
}

type SeoType = 'pet_contest' | 'pet_vote';

const SeoConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [currentTab, setCurrentTab] = useState<SeoType>('pet_contest');
  const [formData, setFormData] = useState<SeoConfig>({
    title: '',
    keywords: '',
    description: '',
    image_path: ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    fetchSeoConfig();
  }, [currentTab]);

  const fetchSeoConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${environment.apiBaseUrl}/seo-config?type=${currentTab}`, {
        headers: getApiHeadersForGet()
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setFormData({
            title: data.title || '',
            keywords: data.keywords || '',
            description: data.description || '',
            image_path: data.image_base64 || data.image_path || ''
          });
          // Usar image_base64 si está disponible, sino image_path
          if (data.image_base64) {
            setImagePreview(data.image_base64);
          } else if (data.image_path) {
            setImagePreview(data.image_path);
          } else {
            setImagePreview('');
          }
        } else {
          // Si no hay datos, limpiar el formulario
          setFormData({
            title: '',
            keywords: '',
            description: '',
            image_path: ''
          });
          setImagePreview('');
        }
        setSelectedFile(null);
      }
    } catch (err) {
      console.error('Error fetching SEO config:', err);
      setError('Error al cargar la configuración SEO');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: SeoType) => {
    setCurrentTab(newValue);
    setError('');
    setSuccess('');
    setSelectedFile(null);
  };

  const handleInputChange = (field: keyof SeoConfig) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    
    // Validar límites de caracteres
    if (field === 'title' && value.length > 60) {
      return;
    }
    if (field === 'keywords' && value.length > 160) {
      return;
    }
    if (field === 'description' && value.length > 300) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tamaño (72 KB = 73728 bytes)
      if (file.size > 73728) {
        setError('La imagen debe ser menor a 72 KB');
        return;
      }
      
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        setError('El archivo debe ser una imagen');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Convertir imagen a base64 si hay un archivo seleccionado
      let imageBase64 = null;
      if (selectedFile) {
        const reader = new FileReader();
        imageBase64 = await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => {
            resolve(reader.result as string);
          };
          reader.onerror = reject;
          reader.readAsDataURL(selectedFile);
        });
      }

      const payload: any = {
        title: formData.title || '',
        keywords: formData.keywords || '',
        description: formData.description || '',
        type: currentTab
      };

      // Siempre enviar la imagen en base64 si está disponible
      if (imageBase64) {
        payload.image = imageBase64;
      } else if (formData.image_path && formData.image_path.startsWith('data:image')) {
        // Si image_path ya es base64 (de una carga anterior), usarlo
        payload.image = formData.image_path;
      }

      const response = await fetch(`${environment.apiBaseUrl}/seo-config`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess('Configuración SEO actualizada exitosamente');
        setFormData(prev => ({
          ...prev,
          image_path: data.image_base64 || data.image_path || prev.image_path
        }));
        setSelectedFile(null);
        // Actualizar preview si hay nueva imagen
        if (data.image_base64) {
          setImagePreview(data.image_base64);
        } else if (data.image_path) {
          setImagePreview(data.image_path);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error al guardar la configuración SEO');
      }
    } catch (err) {
      console.error('Error saving SEO config:', err);
      setError('Error al guardar la configuración SEO');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="napc-page" maxWidth="lg" sx={{ py: 4, minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container className="napc-page" maxWidth="lg" sx={{ py: { xs: 3, md: 6 }, minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowLeft className="w-5 h-5" />}
          onClick={() => navigate(-1)}
          sx={{
            color: '#374151',
            textTransform: 'none',
            mb: 2
          }}
        >
          Volver
        </Button>
        <Box className="napc-eyebrow">Shared surface</Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827' }}>
          Configuración de SEO
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 1 }}>
            SEO Metadata para Páginas
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 4 }}>
            Configura la metadata para páginas estáticas y dinámicas. Estos valores se usarán en las etiquetas meta del HTML.
          </Typography>

          {/* Tabs para seleccionar entre Pet Contest y Pet Vote */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48
                },
                '& .Mui-selected': {
                  color: '#2563eb'
                }
              }}
            >
              <Tab label="Pet Contest" value="pet_contest" />
              <Tab label="Pet Vote" value="pet_vote" />
            </Tabs>
          </Box>

          <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
            {currentTab === 'pet_contest' 
              ? 'Configuración SEO para la página de Pet Contest. Estos datos se usarán en las etiquetas meta.'
              : 'Configuración SEO para la página de Pet Vote. Estos datos se usarán en otra página.'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Title */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#374151' }}>
                  Title (max 60 caracteres):
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Ingrese el título"
                  value={formData.title}
                  onChange={handleInputChange('title')}
                  inputProps={{ maxLength: 60 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
                <Typography variant="caption" sx={{ color: '#6b7280', mt: 0.5, display: 'block' }}>
                  {formData.title?.length || 0}/60 caracteres
                </Typography>
              </Box>

              {/* Keywords */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#374151' }}>
                  Keywords (max 160 caracteres):
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Ingrese las palabras clave separadas por comas"
                  value={formData.keywords}
                  onChange={handleInputChange('keywords')}
                  inputProps={{ maxLength: 160 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
                <Typography variant="caption" sx={{ color: '#6b7280', mt: 0.5, display: 'block' }}>
                  {formData.keywords?.length || 0}/160 caracteres
                </Typography>
              </Box>

              {/* Description */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#374151' }}>
                  Description (max 300 caracteres):
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Ingrese la descripción"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  inputProps={{ maxLength: 300 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
                <Typography variant="caption" sx={{ color: '#6b7280', mt: 0.5, display: 'block' }}>
                  {formData.description?.length || 0}/300 caracteres
                </Typography>
              </Box>

              {/* Image */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#374151' }}>
                  Image (max 72 KB):
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="seo-image-upload"
                  />
                  <label htmlFor="seo-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      sx={{
                        borderColor: '#d1d5db',
                        color: '#374151',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          backgroundColor: '#f9fafb'
                        }
                      }}
                    >
                      Choose File
                    </Button>
                  </label>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {selectedFile ? selectedFile.name : (formData.image_path ? 'Imagen guardada' : 'No file chosen')}
                  </Typography>
                </Box>
                {imagePreview && (
                  <Box sx={{ mt: 2 }}>
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: '200px', 
                        maxHeight: '200px', 
                        borderRadius: '8px',
                        objectFit: 'cover'
                      }} 
                    />
                  </Box>
                )}
              </Box>

              {/* Submit Button */}
              <Box sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save className="w-5 h-5" />}
                  disabled={saving}
                  sx={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    textTransform: 'none',
                    px: 3,
                    py: 1.5,
                    borderRadius: 1,
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#1d4ed8'
                    },
                    '&:disabled': {
                      backgroundColor: '#9ca3af'
                    }
                  }}
                >
                  {saving ? 'Actualizando...' : 'Actualizar SEO'}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SeoConfigPage;

